import React, { useState, useEffect, useRef } from "react";
import { IoAdd } from "react-icons/io5";
import { BsExclamationTriangle } from "react-icons/bs";

// Chromosome list for dropdown
const CHROMOSOMES = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
  "19",
  "20",
  "21",
  "22",
  "X",
  "Y",
];

// Compact chromosome selection component
const ChromosomeSelect = ({ value, onChange }) => (
  <select
    className="form-select form-select-sm"
    value={value}
    onChange={onChange}
    style={{
      paddingLeft: "8px",
      paddingRight: "16px",
      backgroundSize: "10px 6px",
      backgroundPosition: "right 2px center",
      fontFamily: "monospace",
      textAlign: "center",
      fontWeight: "500",
      fontSize: "0.85rem",
      width: "45px",
    }}
  >
    {CHROMOSOMES.map((chr) => (
      <option key={chr} value={chr}>
        {chr}
      </option>
    ))}
  </select>
);

// Compact position input component
const PositionInput = ({ value, onChange, placeholder }) => (
  <input
    type="number"
    className="form-control form-control-sm"
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    min="1"
    style={{ fontSize: "0.875rem" }}
  />
);

// Compact range row component
const RangeRow = ({ range, onRangeChange, onRemove, canRemove = true }) => (
  <div 
    className="position-relative border rounded-3 p-2 mb-2"
    style={{ backgroundColor: 'var(--genomic-range-bg)' }}
  >
    {/* Container with CSS-based responsive layout */}
    <div 
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '0.5rem',
        gridAutoFlow: 'row'
      }}
    >
      {/* Start section */}
      <div className="d-flex gap-2 align-items-center">
        <div style={{ flexShrink: 0 }}>
          <ChromosomeSelect
            value={range.startChr}
            onChange={(e) =>
              onRangeChange({ ...range, startChr: e.target.value })
            }
          />
        </div>
        <div className="flex-fill" style={{ minWidth: 0 }}>
          <PositionInput
            value={range.startPos}
            onChange={(e) =>
              onRangeChange({ ...range, startPos: e.target.value })
            }
            placeholder="Start"
          />
        </div>
      </div>

      {/* End section */}
      <div className="d-flex gap-2 align-items-center">
        <div style={{ flexShrink: 0 }}>
          <ChromosomeSelect
            value={range.endChr}
            onChange={(e) =>
              onRangeChange({ ...range, endChr: e.target.value })
            }
          />
        </div>
        <div className="flex-fill" style={{ minWidth: 0 }}>
          <PositionInput
            value={range.endPos}
            onChange={(e) =>
              onRangeChange({ ...range, endPos: e.target.value })
            }
            placeholder="End"
          />
        </div>
      </div>
    </div>

    {/* X button in top left corner */}
    {canRemove && (
      <button
        type="button"
        className="btn btn-outline-danger btn-sm position-absolute genomic-range-remove-btn btn-animate"
        onClick={onRemove}
        style={{
          top: "-4px",
          left: "-8px",
          width: "18px",
          height: "18px",
          padding: "0",
          fontSize: "0.65rem",
          borderRadius: "50%",
          zIndex: 10,
        }}
      >
        ×
      </button>
    )}
  </div>
);

// Utility function to generate range string from multiple ranges
const generateRangeString = (ranges) => {
  if (!ranges || ranges.length === 0) return "";

  return ranges
    .filter((range) => range.startPos && range.endPos) // Only include complete ranges
    .map((range) => {
      if (range.startChr === range.endChr) {
        return `${range.startChr}:${range.startPos}-${range.endPos}`;
      } else {
        return `${range.startChr}:${range.startPos}-${range.endChr}:${range.endPos}`;
      }
    })
    .join(", ");
};

// Parse string format to multiple ranges
const parseRangeString = (rangeString) => {
  if (!rangeString || !rangeString.trim()) {
    return [
      {
        startChr: "X",
        startPos: "",
        endChr: "X",
        endPos: "",
      },
    ];
  }

  const ranges = rangeString.split(",").map((range) => range.trim());
  const parsedRanges = [];

  for (const range of ranges) {
    // Handle cross-chromosome format: chr1:pos1-chr2:pos2
    const crossChrMatch = range.match(
      /^(?:chr)?([1-9]|1[0-9]|2[0-2]|X|Y):(\d+)-(?:chr)?([1-9]|1[0-9]|2[0-2]|X|Y):(\d+)$/
    );
    if (crossChrMatch) {
      parsedRanges.push({
        startChr: crossChrMatch[1],
        startPos: crossChrMatch[2],
        endChr: crossChrMatch[3],
        endPos: crossChrMatch[4],
      });
      continue;
    }

    // Handle same-chromosome format: chr1:pos1-pos2
    const sameChrMatch = range.match(
      /^(?:chr)?([1-9]|1[0-9]|2[0-2]|X|Y):(\d+)-(\d+)$/
    );
    if (sameChrMatch) {
      parsedRanges.push({
        startChr: sameChrMatch[1],
        startPos: sameChrMatch[2],
        endChr: sameChrMatch[1],
        endPos: sameChrMatch[3],
      });
      continue;
    }
  }

  // If no valid ranges found, return default
  if (parsedRanges.length === 0) {
    return [
      {
        startChr: "X",
        startPos: "",
        endChr: "X",
        endPos: "",
      },
    ];
  }

  return parsedRanges;
};

// Visual picker component
const VisualPicker = ({ ranges, setRanges, onStringChange }) => {
  const onStringChangeRef = useRef(onStringChange);
  onStringChangeRef.current = onStringChange;

  // Update string immediately when ranges change due to user interaction
  useEffect(() => {
    const newString = generateRangeString(ranges);
    onStringChangeRef.current(newString);
  }, [ranges]);

  const updateRange = (index, newRange) => {
    const updatedRanges = [...ranges];
    updatedRanges[index] = newRange;
    setRanges(updatedRanges);
  };

  const removeRange = (index) => {
    if (ranges.length > 1) {
      const updatedRanges = ranges.filter((_, i) => i !== index);
      setRanges(updatedRanges);
    }
  };

  const addRange = () => {
    const newRanges = [
      ...ranges,
      {
        startChr: "X",
        startPos: "",
        endChr: "X",
        endPos: "",
      },
    ];
    setRanges(newRanges);
  };

  return (
    <div>
      {/* Ranges container - no scroll, just keeps going */}
      <div>
        {ranges.map((range, index) => (
          <RangeRow
            key={index}
            range={range}
            onRangeChange={(newRange) => updateRange(index, newRange)}
            onRemove={() => removeRange(index)}
            canRemove={ranges.length > 1}
          />
        ))}
      </div>

      {/* Add Range Button */}
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm w-100 mt-2 d-flex align-items-center justify-content-center gap-1 btn-animate"
        onClick={addRange}
        style={{ padding: "0.375rem 0.75rem" }}
      >
        <IoAdd style={{ fontSize: "0.9rem", flexShrink: 0 }} />
        <span>Add Range</span>
      </button>

      {/* Compact Preview */}
      <div className="bg-light border border-secondary border-opacity-25 rounded-3 p-2 p-sm-3 mt-3">
        <div className="text-muted small fw-semibold mb-1">Preview:</div>
        <div
          className="font-monospace text-body small"
          style={{
            wordBreak: "break-word",
            overflowWrap: "break-word",
            fontSize: "0.85rem",
            lineHeight: "1.4",
          }}
        >
          {generateRangeString(ranges) || "Enter positions above"}
        </div>
      </div>
    </div>
  );
};

// Main genomic range input component with toggle
const GenomicRangeInput = ({
  id,
  value,
  onChange,
  validation,
  placeholder,
  rows = 3,
}) => {
  const [mode, setMode] = useState("picker"); // Start with Visual mode as default
  const [ranges, setRanges] = useState(() => parseRangeString(value));

  // Simple: always update ranges when value changes externally
  useEffect(() => {
    setRanges(parseRangeString(value));
  }, [value]);

  // Handle string mode changes
  const handleStringChange = (e) => {
    onChange(e);
    setRanges(parseRangeString(e.target.value));
  };

  // Handle picker mode changes
  const handlePickerStringChange = (newString) => {
    onChange({ target: { id, value: newString } });
  };

  return (
    <div className="mb-3">
      {/* Simple mode toggle - consistent with existing UI */}
      <div className="d-flex gap-1 mb-3" role="group">
        <button
          type="button"
          className={`btn btn-sm btn-animate flex-fill ${
            mode === "picker" ? "btn-primary" : "btn-outline-secondary"
          }`}
          onClick={() => setMode("picker")}
          style={{ minWidth: 0, padding: "0.25rem 0.5rem" }}
        >
          Visual
        </button>
        <button
          type="button"
          className={`btn btn-sm btn-animate flex-fill ${
            mode === "string" ? "btn-primary" : "btn-outline-secondary"
          }`}
          onClick={() => setMode("string")}
          style={{ minWidth: 0, padding: "0.25rem 0.5rem" }}
        >
          Text
        </button>
      </div>

      {/* Content based on mode */}
      {mode === "string" ? (
        <>
          <textarea
            id={id}
            name={id}
            value={value || ""}
            onChange={handleStringChange}
            className={`form-control ${
              !validation.isValid ? "is-invalid" : ""
            }`}
            placeholder={placeholder || ""}
            rows={rows}
          />
          {!validation.isValid && (
            <div className="invalid-feedback">
              <BsExclamationTriangle className="me-1" />
              {validation.message}
            </div>
          )}
        </>
      ) : (
        <VisualPicker
          ranges={ranges}
          setRanges={setRanges}
          onStringChange={handlePickerStringChange}
        />
      )}
    </div>
  );
};

export default GenomicRangeInput;
